/**
 * @fileoverview Utilities for retrieving and managing note-tag relationships,
 * consolidating repeated logic across the application.
 */

import { apiClient, RequestParams } from './api-client';
import { type Tag } from './tag';
import { type NoteTag } from './note-tag';

/**
 * Result of fetching note-tag relationships.
 */
export interface NoteTagsResult {
  /** All note-tag relationship records */
  noteTags: NoteTag[];
  /** All tag records */
  tags: Tag[];
  /** Map of tag ID to Tag object */
  tagsMap: Map<string, Tag>;
  /** Map of note ID to array of associated Tags */
  noteTagsMap: Map<string, Tag[]>;
}

/**
 * Type guard to check if a tag has a valid ID.
 */
function hasValidId(tag: Tag): tag is Tag & { id: string } {
  return typeof tag.id === 'string' && tag.id.length > 0;
}

/**
 * Type guard to check if a note-tag has valid IDs for both note and tag.
 */
function hasValidNoteTagIds(noteTag: NoteTag): noteTag is NoteTag & { note_id: string; tag_id: string } {
  return (
    typeof noteTag.note_id === 'string' &&
    noteTag.note_id.length > 0 &&
    typeof noteTag.tag_id === 'string' &&
    noteTag.tag_id.length > 0
  );
}

/**
 * Retrieves note-tag relationships with associated tag details.
 * Supports both filtered (by noteId) and unfiltered use cases.
 * 
 * @param noteId - Optional note ID to filter results. If provided, only returns
 *                 note-tags for that specific note. If omitted, returns all note-tags.
 * @returns Promise resolving to note-tags data with lookup maps
 */
export async function getNoteTags(noteId?: string): Promise<NoteTagsResult> {
  const noteTagsParams: RequestParams = {
    page: '0',
    pageSize: '1000',
  };
  
  if (noteId) {
    noteTagsParams.search = noteId;
  }
  
  const noteTagsResponse = await apiClient.getMany<NoteTag>('note_tag', noteTagsParams);
  
  const tagsResponse = await apiClient.getMany<Tag>('tag', {
    page: '0',
    pageSize: '1000',
  });
  
  // Create lookup map from tags with valid IDs
  const tagsMap = new Map<string, Tag>(
    tagsResponse.items
      .filter(hasValidId)
      .map((tag) => [tag.id, tag])
  );
  
  // Group tags by note_id using type guards
  const noteTagsMap = new Map<string, Tag[]>();
  for (const noteTag of noteTagsResponse.items) {
    if (!hasValidNoteTagIds(noteTag)) continue;
    
    const tag = tagsMap.get(noteTag.tag_id);
    if (tag) {
      const existingTags = noteTagsMap.get(noteTag.note_id);
      if (existingTags) {
        existingTags.push(tag);
      } else {
        noteTagsMap.set(noteTag.note_id, [tag]);
      }
    }
  }
  
  return {
    noteTags: noteTagsResponse.items,
    tags: tagsResponse.items,
    tagsMap,
    noteTagsMap,
  };
}
